"use client";

import posthog from "posthog-js";
import { useActionState, useEffect, useRef, useState } from "react";
import {
  requestJobAlert,
  updateJobAlert,
  type AlertActionResult,
} from "@/app/jobs/alert-actions";
import { getAlertLink, subscribeAlertLink } from "@/components/alertLinkStore";
import {
  PREMIUM_PRICE_LABEL,
  UPGRADE_URL,
} from "@/lib/portfolio/entitlements";

/**
 * The board's alert button. It is stateful about the CURRENT search:
 *  - "Create alert"  — no alert yet for this search.
 *  - "Update alert"  — this tab has an alert, but the search has changed since;
 *                      clicking confirms repointing that alert to the new search.
 *  - "Alert active"  — an alert already covers exactly this search.
 * Signed-out users enter an email and a verification link (which creates the
 * free account) doubles as the alert's double opt-in; signed-in users confirm
 * in one click. Both paths land back on the board with a confirmation.
 */
export function JobAlertDialog({
  filters,
  queryText,
  filterLabels,
  isAuthed,
  userEmail,
  userAlertFilters,
  alertCap = 2,
}: {
  /** Alert-scope querystring for the current search ("" = all jobs). */
  filters: string;
  /** The keyword search this alert covers, if any. */
  queryText?: string;
  /** Human labels for each active filter (rendered as chips; compacted if many). */
  filterLabels?: string[];
  isAuthed: boolean;
  /** The signed-in user's address (shown so the one-click path is transparent). */
  userEmail?: string;
  /** The signed-in user's active-alert filter querystrings (for the button state). */
  userAlertFilters?: string[];
  /** This user's plan cap on alerts (free 2 / paid 20) — shown in the copy. */
  alertCap?: number;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [result, createAction, createPending] = useActionState<
    AlertActionResult | null,
    FormData
  >(requestJobAlert, null);
  const [updateResult, updateAction, updatePending] = useActionState<
    AlertActionResult | null,
    FormData
  >(updateJobAlert, null);

  // Which search the active tab's alert covers (from SearchTabs).
  const [linkedQs, setLinkedQs] = useState<string | null>(null);
  useEffect(() => {
    setLinkedQs(getAlertLink());
    return subscribeAlertLink(setLinkedQs);
  }, []);

  const alerts = userAlertFilters ?? [];
  const isActive = alerts.includes(filters);
  const canUpdate =
    !isActive && linkedQs != null && linkedQs !== filters && alerts.includes(linkedQs);
  const mode: "active" | "update" | "create" = isActive
    ? "active"
    : canUpdate
      ? "update"
      : "create";

  const labels = filterLabels ?? [];
  const MAX_CHIPS = 6;
  const shownFilters = labels.slice(0, MAX_CHIPS);
  const hiddenFilters = labels.length - shownFilters.length;
  const hasScope = Boolean(queryText) || labels.length > 0;

  const open = () => {
    dialogRef.current?.showModal();
    if (posthog.__loaded)
      posthog.capture("job_alert_dialog_opened", { authed: isAuthed, mode });
  };
  const close = () => dialogRef.current?.close();

  const linkSent = result?.ok && result.state === "link_sent";

  const trigger = {
    create: {
      label: "Create alert",
      cls: "border-brand-200 bg-brand-50 text-brand-700 hover:border-brand-400 hover:bg-brand-100",
    },
    update: {
      label: "Update alert",
      cls: "border-brand-600 bg-brand-600 text-white hover:bg-brand-700",
    },
    active: {
      label: "Alert active",
      cls: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    },
  }[mode];

  const ScopeCard = () =>
    hasScope ? (
      <div className="mt-3 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2.5">
        <p className="telex text-[8.5px] text-neutral-400">This alert covers</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {queryText ? (
            <span className="inline-flex items-center rounded-full border border-ink/20 bg-white px-2 py-0.5 text-[12px] font-medium text-ink">
              <span className="telex mr-1 text-[7.5px] text-neutral-400">search</span>
              &ldquo;{queryText}&rdquo;
            </span>
          ) : null}
          {shownFilters.map((label, i) => (
            <span
              key={`${label}-${i}`}
              className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-[12px] font-medium text-brand-800"
            >
              {label}
            </span>
          ))}
          {hiddenFilters > 0 ? (
            <span className="inline-flex items-center px-1 py-0.5 text-[12px] text-neutral-500">
              +{hiddenFilters} more
            </span>
          ) : null}
        </div>
      </div>
    ) : (
      <p className="mt-2 text-[13px] text-neutral-500">
        Covering{" "}
        <span className="font-medium text-ink">all international-organization jobs</span>.
      </p>
    );

  return (
    <>
      <button
        type="button"
        onClick={open}
        aria-haspopup="dialog"
        className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold transition-colors ${trigger.cls}`}
      >
        {mode === "active" ? (
          <svg aria-hidden width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M3.5 8.5 7 12l5.5-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg aria-hidden width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M8 2a4 4 0 0 0-4 4v2.5L2.6 11a.7.7 0 0 0 .6 1.1h9.6a.7.7 0 0 0 .6-1.1L12 8.5V6a4 4 0 0 0-4-4Zm-1.5 11a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {trigger.label}
      </button>

      <dialog
        ref={dialogRef}
        className="pitch-dialog"
        aria-labelledby="alert-title"
        onClick={(e) => {
          if (e.target === dialogRef.current) close();
        }}
      >
        <div className="overflow-hidden rounded-xl border-2 border-ink bg-white">
          <div className="p-6 sm:p-7">
            <div className="flex items-start justify-between gap-4">
              <p className="telex flex items-center gap-2 text-[9.5px] text-brand-700">
                <span aria-hidden className="inline-block h-[6px] w-[6px] bg-current" />
                {mode === "update" ? "Update alert" : mode === "active" ? "Your alert" : "Daily job alert"}
              </p>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="-mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded-full text-lg leading-none text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
              >
                ×
              </button>
            </div>

            {mode === "active" ? (
              <>
                <h2 id="alert-title" className="display-calm mt-3 text-2xl font-semibold leading-tight text-ink">
                  This alert is on.
                </h2>
                <p className="mt-3 text-[14.5px] leading-relaxed text-neutral-600">
                  We&rsquo;ll email new jobs matching this search as they appear —
                  only ones you haven&rsquo;t seen before.
                </p>
                <ScopeCard />
                <div className="mt-4 rounded-md border border-neutral-200 bg-neutral-50 px-3.5 py-3 text-[13.5px] leading-relaxed text-neutral-600">
                  Pause or delete it anytime from your{" "}
                  <a href="/dashboard" className="font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800">
                    dashboard
                  </a>
                  .
                </div>
                <button
                  type="button"
                  onClick={close}
                  className="mt-5 inline-flex h-11 w-full items-center justify-center rounded-md border border-neutral-300 px-5 text-sm font-semibold text-ink transition-colors hover:bg-neutral-50"
                >
                  Keep browsing
                </button>
              </>
            ) : mode === "update" ? (
              <>
                <h2 id="alert-title" className="display-calm mt-3 text-2xl font-semibold leading-tight text-ink">
                  Update this alert?
                </h2>
                <p className="mt-3 text-[14.5px] leading-relaxed text-neutral-600">
                  You changed the search. Point your existing alert at what
                  you&rsquo;re looking at now:
                </p>
                <ScopeCard />
                <form action={updateAction} className="mt-4">
                  <input type="hidden" name="oldFilters" value={linkedQs ?? ""} />
                  <input type="hidden" name="filters" value={filters} />
                  {updateResult && !updateResult.ok ? (
                    <p className="mb-3 rounded-md border border-stamp-100 bg-stamp-50 px-3 py-2 text-sm text-stamp-700">
                      {updateResult.message}
                    </p>
                  ) : null}
                  <button
                    type="submit"
                    disabled={updatePending}
                    className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
                  >
                    {updatePending ? "Updating…" : "Update my alert"}
                  </button>
                  <p className="telex mt-3 text-center text-[8.5px] text-neutral-400">
                    Your alert will track this search instead — from now on
                  </p>
                </form>
              </>
            ) : (
              <>
                <h2 id="alert-title" className="display-calm mt-3 text-2xl font-semibold leading-tight text-ink">
                  Never miss a posting.
                </h2>
                {linkSent ? (
                  <div className="mt-4 rounded-md border border-brand-200 bg-brand-50 px-3.5 py-3.5 text-[14px] leading-relaxed text-brand-900">
                    <p className="font-semibold text-ink">Check your inbox.</p>
                    <p className="mt-1">
                      {result?.message ?? "We sent you a link."} Click it to
                      confirm — that creates your account and switches this
                      alert on, then drops you right back on this search.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="mt-3 text-[14.5px] leading-relaxed text-neutral-600">
                      Get an email when new jobs match this search — only new
                      listings, never repeats.
                    </p>
                    <ScopeCard />
                    <p className="telex mt-3 text-[8.5px] leading-relaxed text-neutral-400">
                      Create up to {alertCap} alert{alertCap === 1 ? "" : "s"} ·
                      one email each, only when something new lands
                    </p>
                    <form action={createAction} className="mt-4">
                      <input type="hidden" name="filters" value={filters} />
                      {isAuthed ? (
                        <p className="rounded-md border border-neutral-200 bg-neutral-50 px-3.5 py-2.5 text-[13.5px] text-neutral-600">
                          We&rsquo;ll send it to{" "}
                          <span className="font-medium text-ink">
                            {userEmail ?? "your account email"}
                          </span>
                          .
                        </p>
                      ) : (
                        <label className="block">
                          <span className="block text-[13px] font-medium text-ink">
                            Your email
                          </span>
                          <input
                            type="email"
                            name="email"
                            required
                            autoFocus
                            placeholder="you@example.org"
                            className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-3.5 py-2.5 text-[15px] text-ink placeholder:text-neutral-400 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
                          />
                          <span className="mt-1.5 block text-[12px] text-neutral-500">
                            We&rsquo;ll email a link to confirm — no password needed.
                          </span>
                        </label>
                      )}
                      {result && !result.ok ? (
                        result.upgrade ? (
                          <div className="mt-3 rounded-lg border border-brand-200 bg-brand-50 px-3.5 py-3">
                            <p className="text-[13px] leading-relaxed text-ink">
                              {result.message}
                            </p>
                            <a
                              href={UPGRADE_URL}
                              className="mt-2.5 inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13.5px] font-semibold text-white transition-colors hover:bg-brand-700"
                            >
                              <span aria-hidden>✦</span> Upgrade to Premium —
                              only {PREMIUM_PRICE_LABEL}
                              <span aria-hidden>→</span>
                            </a>
                          </div>
                        ) : (
                          <p className="mt-3 rounded-md border border-stamp-100 bg-stamp-50 px-3 py-2 text-sm text-stamp-700">
                            {result.message}
                          </p>
                        )
                      ) : null}
                      <button
                        type="submit"
                        disabled={createPending}
                        className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-md bg-brand-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {createPending
                          ? "Setting up…"
                          : isAuthed
                            ? "Turn on my daily alert"
                            : "Email me a confirmation link"}
                      </button>
                      <p className="telex mt-3 text-center text-[8.5px] text-neutral-400">
                        One email a day · unsubscribe anytime ·{" "}
                        <a href="/privacy" className="underline hover:text-brand-700">
                          privacy
                        </a>
                      </p>
                    </form>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </dialog>
    </>
  );
}
